/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {LookupService} from './lookup.service';
import {PlanModelService} from './plan-model.service';
import {SpatialSequenceService} from './spatial-sequence.service';
import {SpatialUtilService} from '../../design/services/spatial-util.service';
import {of, tap} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {SP_PREFIXED_PARENT_PLAN_TYPES} from '../constants';

@Injectable({
    providedIn: 'root'
})
export class AffectedLotReconciliationService {
    title = 'AffectedLotReconciliationService';

    constructor(
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private spatialUtilService: SpatialUtilService,
        private spatialSequenceService: SpatialSequenceService
    ) {}

    process() {
        const logPrefix = `${this.title} - Reconcile affected lots`;
        console.info(logPrefix);

        const model = this.planModelService.getModel();

        if (model.pdfCreationMethod !== 'SURVEYOR_PORTAL') {
            return of([]);
        }

        const planPurposeLookup = this.lookupService.getLookups('planPurpose').find(lookup => lookup.id === model.planPurpose.id);

        if (!planPurposeLookup) {
            return of([]);
        }

        const tenures = this.planModelService.getModel().formerTenures.reduce((acc, tenure) => {
            if (tenure.lots.length) {
                acc.push(...tenure.lots.map(lot => ({tenure: tenure, lot: lot})));
            } else {
                acc.push({tenure: tenure, lot: null});
            }
            return acc;
        }, []);

        let observable = of(null);

        if (planPurposeLookup.affectedLotLandSynchronisation) {
            const guid = this.planModelService.getGuid();
            // Add / update affected lot annotations
            const affectedLots = tenures.map(v => {
                // [SubjectLandDescription]  |  [LotLandName]  |  [PlanNumber]  |  [TitleReference]
                // Double-space before and after "|" symbol.
                // [LotLandName]:
                // - If Survey-Strata Plan and COMMON PROPERTY => "CP[LotNumber]"
                // - If Strata Plan and COMMON PROPERTY => "COMMON PROPERTY"
                // - Else => "[LotNumber]"
                //
                // [TitleReference]:
                // - If Parent plan type = 'No lot on plan identifier' => "[TenureTitle]"
                //
                // Examples:
                // 1  |  DP123456
                // CROWN ALLOTMENT 121  |  1385-66
                // COMMON PROPERTY  |  SP7625
                // 2  |  SP7625
                // CP4  |  SP8263
                // 5  |  SP8263

                const tenure = v.tenure;
                const lot = v.lot;

                const planTypeCode = tenure.parentPlanType ? tenure.parentPlanType.code : 'NLOP';

                const affectedLotLandParts = [];

                // SubjectLandDescription
                if (tenure.subjectLandDescription) {
                    affectedLotLandParts.push(tenure.subjectLandDescription);
                }

                // LotLandName
                if (lot) {
                    if (lot.parentLot && lot.parentLotType === 'CP') {
                        affectedLotLandParts.push(`CP${lot.parentLot}`);
                    } else if (lot.nonLotCommonProperty) {
                        affectedLotLandParts.push(tenure.parentPlanType.manualCommonPropertyLotTypeSelection);
                    } else if (lot.parentLot) {
                        affectedLotLandParts.push(`${lot.parentLot}`);
                    }
                }

                // PlanNumber
                let planNumber = null;
                if (tenure.parentPlan) {
                    const planTypePrefix = SP_PREFIXED_PARENT_PLAN_TYPES.includes(planTypeCode) ? 'SP' : planTypeCode;
                    planNumber = `${planTypePrefix}${tenure.parentPlan}`;
                    affectedLotLandParts.push(planNumber);
                }

                // TitleReference
                if (planTypeCode === 'NLOP') {
                    affectedLotLandParts.push(tenure.title);
                }

                const planTypeDesc =
                    (tenure.parentPlanType && tenure.parentPlanType.name) || (tenure.subjectLandType && tenure.subjectLandType.description) || null;

                return {
                    attributes: {
                        guid: guid,
                        affectedtenureid: tenure.id,
                        affectedlotland: affectedLotLandParts.join('  |  '),
                        plannumber: planNumber ? `${planNumber}` : null,
                        plantypedesc: planTypeDesc,
                        lotnumber: lot && lot.parentLot ? `${lot.parentLot}` : null,
                        commonproperty: lot && (lot.nonLotCommonProperty || lot.parentLotType === 'CP') ? 'TRUE' : 'FALSE',
                        subjectlanddescription: tenure.subjectLandDescription || null,
                        titlereference: tenure.title || (lot && lot.titles && lot.titles.length && lot.titles.join()) || null,
                        type: 1
                    }
                };
            });

            // Get existing affected lots
            const existingAffectedLots = this.spatialUtilService.getAffectedLots();

            console.info(`${logPrefix} - query callback - ${existingAffectedLots.length} affected lots`);

            let adds = null,
                deletes = null;
            const deleteFeatureIdsIn = [],
                deleteFeatureIdsNotIn = [];

            if (existingAffectedLots.length === 0) {
                if (affectedLots.length) {
                    adds = affectedLots;
                }
            } else {
                const oldAttrsList = existingAffectedLots.map(v => v.attributes);
                const newAttrsList = affectedLots.map(v => v.attributes);

                // Add any features that don't already exist (match on new attribute keys i.e. don't include id)
                const addsAttrs = newAttrsList.filter(attrs => !oldAttrsList.find(v => Object.keys(attrs).every(k => attrs[k] === v[k])));
                if (addsAttrs.length) {
                    adds = addsAttrs.map(v => ({attributes: v}));
                }

                // Delete any features no longer required (match on new attribute keys i.e. don't include id)
                const deletesAttrs = oldAttrsList.filter(attrs => !newAttrsList.find(v => Object.keys(v).every(k => attrs[k] === v[k])));
                if (deletesAttrs.length) {
                    deletes = this.spatialUtilService.toApplyEditsDeletes('affectedlot', deletesAttrs);
                    deleteFeatureIdsIn.push(...deletesAttrs.map(v => v.id));
                }

                deleteFeatureIdsNotIn.push(...oldAttrsList.map(v => v.id));
            }

            if (adds && adds.length) {
                const sequence = this.spatialSequenceService.getAffectedLotSequence(adds.length);
                adds.forEach((v, i) => (v.attributes.id = sequence[i]));
            }

            if (deleteFeatureIdsIn.length || deleteFeatureIdsNotIn.length) {
                //Delete affected lot annotations
                let annotations = this.spatialUtilService.getAnnotations();
                if (annotations.length) {
                    annotations = annotations.filter(
                        a =>
                            a.attributes.featuretype === 'affectedLot' &&
                            (deleteFeatureIdsIn.includes(a.attributes.featureid) || !deleteFeatureIdsNotIn.includes(a.attributes.featureid))
                    );
                }

                if (annotations.length) {
                    observable = observable.pipe(
                        switchMap(() => {
                            return this.spatialUtilService.applyEdits(
                                'annotation',
                                null,
                                null,
                                this.spatialUtilService.toApplyEditsDeletes('annotation', annotations)
                            );
                        }),
                        tap((res: any) => {
                            const numDeletes = (res.deletes && res.deletes.length) || 0;
                            console.info(`${logPrefix} - ${numDeletes} affected lot annotations deleted`);
                        })
                    );
                }
            }

            if (adds || deletes) {
                observable = observable.pipe(
                    switchMap(() => this.spatialUtilService.applyEdits('affectedLot', adds, null, deletes)),
                    tap((res: any) => {
                        const numAdds = (res.adds && res.adds.length) || 0;
                        const numDeletes = (res.deletes && res.deletes.length) || 0;
                        console.info(`${logPrefix} - ${numAdds} affected lots added, ${numDeletes} affected lots deleted`);
                    })
                );
            }

            return observable;
        } else {
            // Get all affected lot annotations
            const annotations = this.spatialUtilService.getAnnotations().filter(f => f.attributes.featuretype === 'affectedLot');

            return observable.pipe(
                switchMap(() => {
                    if (annotations.length === 0) {
                        return of(null);
                    }
                    return this.spatialUtilService
                        .applyEdits('annotation', null, null, this.spatialUtilService.toApplyEditsDeletes('annotation', annotations))
                        .pipe(tap((res: any) => console.info(`${logPrefix} - ${res.deletes.length} affected lot annotations deleted`)));
                }),
                switchMap(() => {
                    const affectedLots = this.spatialUtilService.getAffectedLots();

                    if (affectedLots.length === 0) {
                        return of(null);
                    }

                    //Delete all affected lots
                    return this.spatialUtilService.applyEdits(
                        'affectedLot',
                        null,
                        null,
                        this.spatialUtilService.toApplyEditsDeletes('affectedlot', affectedLots)
                    );
                })
            );
        }

        // TODO PAU-650
        // appPromises.push(this.promise);
    }
}
